// src/pages/orders-page.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from './components/Header';
import { 
  Package,
  Truck,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  ChevronRight,
  ShoppingBag
} from 'lucide-react';
import { authAPI } from './services/api';

const OrdersPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('all');

  // 檢查登入狀態
  useEffect(() => {
    if (!authAPI.isAuthenticated()) {
      alert(t('errors.loginRequired') || '請先登入');
      navigate('/auth');
      return;
    }
  }, [navigate, t]);

  // 從 URL 參數獲取狀態
  useEffect(() => {
    const status = searchParams.get('status');
    if (status) {
      setActiveTab(status);
    }
  }, [searchParams]);

  // 訂單狀態標籤
  const orderTabs = [
    { 
      key: 'all', 
      label: t('orders.all') || '全部訂單', 
      icon: FileText,
      count: 0
    },
    { 
      key: 'pending', 
      label: t('orders.pending') || '待付款', 
      icon: Clock,
      count: 0
    },
    { 
      key: 'processing', 
      label: t('orders.processing') || '處理中', 
      icon: Package,
      count: 0
    },
    { 
      key: 'shipping', 
      label: t('orders.shipping') || '運送中', 
      icon: Truck,
      count: 0
    },
    { 
      key: 'completed', 
      label: t('orders.completed') || '已完成', 
      icon: CheckCircle,
      count: 0
    },
    { 
      key: 'cancelled', 
      label: t('orders.cancelled') || '已取消', 
      icon: XCircle,
      count: 0
    }
  ];

  // 空狀態圖示組件
  const EmptyState = () => (
    <div className="flex flex-col items-center justify-center py-20 px-4">
      <div className="w-32 h-32 mb-6 rounded-full bg-gray-100 flex items-center justify-center">
        <ShoppingBag className="w-16 h-16 text-gray-400" />
      </div>
      <h3 className="text-2xl font-bold text-gray-800 mb-2">
        {t('orders.noOrders') || '還沒有訂單'}
      </h3>
      <p className="text-gray-500 text-center mb-8 max-w-md">
        {activeTab === 'all' 
          ? (t('orders.noOrdersDescription') || '您還沒有任何訂單記錄，快去選購心儀的商品吧！')
          : (t('orders.noOrdersInStatus') || `目前沒有${orderTabs.find(tab => tab.key === activeTab)?.label}的訂單`)}
      </p>
      <button
        onClick={() => navigate('/products')}
        className="bg-orange-500 text-white px-8 py-3 rounded-lg font-semibold hover:bg-orange-600 transition-colors flex items-center gap-2"
      >
        <ShoppingBag className="w-5 h-5" />
        {t('orders.startShopping') || '開始購物'}
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ✅ 頂部導航欄 - 使用 simple variant */}
      <Header variant="simple" title={t('orders.myOrders') || '我的訂單'} />

      {/* 主要內容 */}
      <div className="container mx-auto px-4 py-8">
        {/* 麵包屑導航 */}
        <div className="flex items-center gap-2 text-sm text-gray-600 mb-6">
          <button 
            onClick={() => navigate('/')}
            className="hover:text-orange-500"
          >
            {t('common.home') || '首頁'}
          </button>
          <ChevronRight className="w-4 h-4" />
          <span className="text-gray-900 font-medium">{t('orders.myOrders') || '我的訂單'}</span>
        </div>

        {/* 頁面標題 */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {t('orders.myOrders') || '我的訂單'}
          </h1>
          <p className="text-gray-600">
            {t('orders.ordersDescription') || '查看和管理您的所有訂單'}
          </p>
        </div>

        {/* 訂單狀態標籤 */}
        <div className="bg-white rounded-lg shadow-sm mb-6 overflow-x-auto">
          <div className="flex border-b">
            {orderTabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-2 px-6 py-4 whitespace-nowrap transition-colors relative ${
                    activeTab === tab.key
                      ? 'text-orange-500 border-b-2 border-orange-500 font-semibold'
                      : 'text-gray-600 hover:text-orange-500 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{tab.label}</span>
                  {tab.count > 0 && (
                    <span className="bg-orange-500 text-white text-xs rounded-full px-2 py-0.5 min-w-[20px] text-center">
                      {tab.count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* 訂單列表區域 */}
        <div className="bg-white rounded-lg shadow-sm min-h-[500px]">
          {/* 空狀態 */}
          <EmptyState />

          {/* 訂單列表 - 這裡之後可以添加實際的訂單數據 */}
          {/* 
          <div className="divide-y">
            {orders.map((order) => (
              <OrderItem key={order.id} order={order} />
            ))}
          </div>
          */}
        </div>

        {/* 幫助提示 */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
            <FileText className="w-5 h-5" />
            {t('orders.helpTitle') || '訂單相關幫助'}
          </h3>
          <ul className="text-sm text-blue-800 space-y-2">
            <li>• {t('orders.helpTip1') || '您可以在此頁面查看所有訂單的詳細信息'}</li>
            <li>• {t('orders.helpTip2') || '點擊訂單可查看詳細內容和物流信息'}</li>
            <li>• {t('orders.helpTip3') || '訂單完成後可以對商品進行評價'}</li>
            <li>• {t('orders.helpTip4') || '如有問題，請聯繫客服協助處理'}</li>
          </ul>
        </div>
      </div>

      {/* 頁腳 */}
      <footer className="bg-gray-800 text-white mt-12">
        <div className="container mx-auto px-4 py-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h3 className="font-bold mb-4">{t('footer.aboutUs') || '關於我們'}</h3>
              <ul className="space-y-2 text-sm text-gray-300">
                <li><button onClick={() => navigate('/about')}>{t('footer.companyInfo') || '公司資訊'}</button></li>
                <li><button onClick={() => navigate('/careers')}>{t('footer.careers') || '人才招募'}</button></li>
                <li><button onClick={() => navigate('/press')}>{t('footer.press') || '媒體報導'}</button></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold mb-4">{t('footer.customerService') || '客戶服務'}</h3>
              <ul className="space-y-2 text-sm text-gray-300">
                <li><button onClick={() => navigate('/help')}>{t('footer.helpCenter') || '幫助中心'}</button></li>
                <li><button onClick={() => navigate('/shipping')}>{t('footer.shipping') || '配送資訊'}</button></li>
                <li><button onClick={() => navigate('/returns')}>{t('footer.returns') || '退換貨政策'}</button></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold mb-4">{t('footer.myAccount') || '我的帳戶'}</h3>
              <ul className="space-y-2 text-sm text-gray-300">
                <li><button onClick={() => navigate('/orders')} className="text-orange-400">{t('footer.orderTracking') || '訂單追蹤'}</button></li>
                <li><button onClick={() => navigate('/favorites')}>{t('footer.wishlist') || '我的收藏'}</button></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold mb-4">{t('footer.followUs') || '關注我們'}</h3>
              <div className="flex gap-4">
                <a href="#" className="text-2xl hover:text-orange-500">📘</a>
                <a href="#" className="text-2xl hover:text-orange-500">📷</a>
                <a href="#" className="text-2xl hover:text-orange-500">🐦</a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-sm text-gray-400">
            {t('footer.copyright') || '© 2024 PG Mall. All rights reserved.'}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default OrdersPage;